import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ApiClientError,
  CITIES,
  CITY_REGION_MAP,
  type City,
  type Specialization,
} from '@pomoshnik/shared';
import { apiClient } from '@/lib/apiClient';
import { useAuthStore } from '@/lib/stores/authStore';
import { ProfileEditView, type SaveState } from './ProfileEditView';
import { PhoneChangeModal } from './PhoneChangeModal';

/**
 * Container Экрана 2.11.2 «Редактирование профиля» — Block 11 Phase 11.B (ADR-0091).
 *
 * Держит state + side effects; UI — чистый presenter `ProfileEditView`.
 *
 * **Источники данных (без GET — ADR §3):** форма пред-заполняется из `authStore`
 * (`profile.name`/`profile.specialization` + `city`). Отдельный `GET /api/profile`
 * в 11.B НЕ нужен — ответ `PUT` синкает `authStore` через `setProfileFields`.
 *
 * **Город — только свой регион (P-008):** дропдаун отфильтрован
 * `CITIES.filter(c => CITY_REGION_MAP[c] === region)`. Бэк перепроверяет инвариант.
 *
 * **Телефон — отдельный OTP-под-флоу (мок, P-010):** «Изменить номер» открывает
 * `PhoneChangeModal`; успех обновляет ТОЛЬКО локальный показ (телефон вне
 * контракта `profile.ts`, §1a; реальные эндпоинты OTP — задача 1.2).
 *
 * **5 состояний P-021** для сохранения: idle/loading/success/error.
 */

const GENERIC_SAVE_ERROR = 'Не удалось сохранить профиль. Попробуйте ещё раз.';
const NETWORK_SAVE_ERROR = 'Нет связи с сервером. Проверьте интернет и попробуйте ещё раз.';

export function ProfileEdit() {
  const navigate = useNavigate();

  const profile = useAuthStore((s) => s.profile);
  const storeCity = useAuthStore((s) => s.city);
  const region = useAuthStore((s) => s.region);
  const lang = useAuthStore((s) => s.lang);
  const setProfileFields = useAuthStore((s) => s.setProfileFields);

  // Пред-заполнение из authStore (P-009: profile может быть null после refresh —
  // тогда поля пустые, город из persisted authStore.city).
  const [name, setName] = useState<string>(profile?.name ?? '');
  const [specialization, setSpecialization] = useState<Specialization | ''>(
    profile?.specialization ?? '',
  );
  const [city, setCity] = useState<City | ''>(storeCity ?? '');
  const [displayedPhone, setDisplayedPhone] = useState<string | null>(profile?.phone ?? null);

  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [nameError, setNameError] = useState<string | undefined>();
  const [specializationError, setSpecializationError] = useState<string | undefined>();
  const [formError, setFormError] = useState<string | undefined>();
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);

  // Города своего региона (P-008). region null (degraded) → пустой список.
  const cityOptions = useMemo<ReadonlyArray<City>>(
    () => (region === null ? [] : CITIES.filter((c) => CITY_REGION_MAP[c] === region)),
    [region],
  );

  const handleSave = async () => {
    setNameError(undefined);
    setSpecializationError(undefined);
    setFormError(undefined);

    const trimmedName = name.trim();
    let hasError = false;
    if (trimmedName.length === 0) {
      setNameError('Введите имя');
      hasError = true;
    }
    if (specialization === '') {
      setSpecializationError('Выберите специализацию');
      hasError = true;
    }
    if (city === '') {
      setFormError('Выберите город');
      hasError = true;
    }
    if (hasError || specialization === '' || city === '') return;

    setSaveState('loading');
    try {
      const res = await apiClient.profile.update({
        name: trimmedName,
        specialization,
        city,
      });
      // Синк authStore из эхо-ответа (имя/специализация/город; телефон вне правки).
      setProfileFields({
        name: res.name,
        specialization: res.specialization,
        city: res.city,
      });
      setSaveState('success');
      navigate('/cabinet');
    } catch (err) {
      setSaveState('error');
      const message =
        err instanceof ApiClientError && err.code === 'NETWORK_ERROR'
          ? NETWORK_SAVE_ERROR
          : GENERIC_SAVE_ERROR;
      setFormError(message);
    }
  };

  return (
    <>
      <ProfileEditView
        name={name}
        specialization={specialization}
        city={city}
        cityOptions={cityOptions}
        lang={lang}
        phone={displayedPhone}
        saveState={saveState}
        nameError={nameError}
        specializationError={specializationError}
        formError={formError}
        onNameChange={setName}
        onSpecializationChange={setSpecialization}
        onCityChange={setCity}
        onChangePhone={() => setPhoneModalOpen(true)}
        onSave={handleSave}
      />
      {phoneModalOpen && region !== null ? (
        <PhoneChangeModal
          region={region}
          onClose={() => setPhoneModalOpen(false)}
          onConfirmed={(newPhone) => {
            // МОК: успех OTP обновляет только локальный показ (телефон вне profile.ts).
            setDisplayedPhone(newPhone);
            setPhoneModalOpen(false);
          }}
        />
      ) : null}
    </>
  );
}
